import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtOrEmpty } from "@/lib/format";
import { Loader2, Calculator, Search, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { HPP_VIEW, PROFIT_VIEW } from "@/lib/constants";

const STATUS_BADGE = { Aman: "bg-emerald-50 text-emerald-700", Waspada: "bg-amber-50 text-amber-700", Rugi: "bg-red-50 text-red-700", "Belum Lengkap": "bg-slate-100 text-slate-600" };

export default function Hpp() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const money = user && PROFIT_VIEW.includes(user.role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => { setLoading(true); api.get("/hpp", { params: q ? { q } : {} }).then(({ data }) => setList(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4" data-testid="hpp-page">
      {user && !HPP_VIEW.includes(user.role) ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="access-denied">Akses Ditolak. HPP hanya untuk Owner, Manager, dan Finance.</Card>
      ) : (<>
      <div><h1 className="font-heading text-2xl font-bold text-navy">HPP Kendaraan</h1><p className="text-sm text-muted-foreground">{list.length} kendaraan · harga pokok per unit</p></div>
      <Card className="p-3"><form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari proyek, customer, no. polisi..." className="pl-9" data-testid="hpp-search" />
      </form></Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><Calculator className="mx-auto mb-2 opacity-40" /> Belum ada data HPP.</Card> : (
          <>
            <Card className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100"><tr className="text-left text-xs">
                  <th className="p-2.5">Proyek</th><th className="p-2.5">Kendaraan</th>{money && <><th className="p-2.5 text-right">Nilai Kontrak</th>
                  <th className="p-2.5 text-right">RAB</th><th className="p-2.5 text-right">HPP Aktual</th><th className="p-2.5 text-right">Selisih</th><th className="p-2.5 text-right">Laba Kotor</th><th className="p-2.5 text-right">Margin</th><th className="p-2.5">Status</th></>}<th></th>
                </tr></thead>
                <tbody>
                  {list.map((h) => (
                    <tr key={`${h.project_uuid}-${h.vehicle_id}`} data-testid={`hpp-row-${h.project_id}`} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/hpp/${h.project_uuid}/${h.vehicle_id}`)}>
                      <td className="p-2.5 font-mono-k3 text-xs text-primary">{h.project_id}</td>
                      <td className="p-2.5">{h.plate} · {h.brand}<div className="text-[10px] text-muted-foreground">{h.customer_name} · {h.work_type}</div></td>
                      {money && <>
                      <td className="p-2.5 text-right" data-testid={`hpp-contract-${h.project_id}`}>{h.contract_total != null ? fmtOrEmpty(h.contract_total) : h.deal_exists === false ? "BELUM DEAL" : "—"}</td>
                      <td className="p-2.5 text-right">{fmtOrEmpty(h.rab_plan)}</td>
                      <td className="p-2.5 text-right">{fmtOrEmpty(h.hpp_actual)}</td>
                      <td className={`p-2.5 text-right ${h.budget_status === "Over Budget" ? "text-red-600" : "text-emerald-700"}`}>{fmtOrEmpty(h.variance, "—")}</td>
                      <td className={`p-2.5 text-right font-medium ${h.laba_kotor < 0 ? "text-red-600" : "text-emerald-600"}`}>{h.laba_kotor != null ? fmtOrEmpty(h.laba_kotor) : "—"}{h.profit_status && <div className="text-[9px] text-muted-foreground">{h.profit_status}</div>}</td>
                      <td className="p-2.5 text-right">{h.margin_percent != null ? `${h.margin_percent}%` : "-"}</td>
                      <td className="p-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[h.status]}`}>{h.status}</span></td>
                      </>}
                      <td className="p-2.5"><ArrowRight size={14} className="text-muted-foreground" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <div className="md:hidden space-y-2">
              {list.map((h) => (
                <Card key={`${h.project_uuid}-${h.vehicle_id}`} data-testid={`hpp-card-${h.project_id}`} className="p-3" onClick={() => navigate(`/hpp/${h.project_uuid}/${h.vehicle_id}`)}>
                  <div className="flex items-center justify-between"><span className="font-mono-k3 text-xs text-primary">{h.project_id}</span>{h.status && <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[h.status]}`}>{h.status}</span>}</div>
                  <div className="font-semibold">{h.plate} · {h.customer_name}</div>
                  {money && <div className="text-xs">HPP: {fmtOrEmpty(h.hpp_actual)} · Laba: <b className={h.laba_kotor < 0 ? "text-red-600" : "text-emerald-600"}>{h.laba_kotor != null ? fmtOrEmpty(h.laba_kotor) : "—"}</b> ({h.margin_percent != null ? `${h.margin_percent}%` : "-"})</div>}
                </Card>
              ))}
            </div>
          </>
        )
      )}
      </>)}
    </div>
  );
}
